import wordmark from '../assets/logo-wordmark.png';
import mark from '../assets/logo-mark.png';
import { cn } from '../lib/cn';

/**
 * The NuDent lockup.
 *
 * ── Where the artwork comes from ────────────────────────────────────────────
 *
 * The desktop repository (`nudent/assets/logo_text.png`), which is the only
 * place the real mark exists. It is re-exported here at web size rather than
 * referenced across repositories, so this one is the source for every portal
 * and a change is one file plus a `shared:sync`.
 *
 * Downscaled from 800x258 (91 KB) to 198x64 (12 KB). The header renders it at
 * about 26px tall, so 64px covers a retina screen with room to spare, and the
 * original was carrying roughly eight times the pixels anything would use.
 *
 * ── Why not the SVG that sits beside it ─────────────────────────────────────
 *
 * `logo_text.svg` in that repository is not a vector: it contains zero <path>
 * elements and one base64 PNG inside an SVG wrapper, plus a drop-shadow filter,
 * at 126 KB. It would cost more than the PNG and scale no better. If a true
 * vector is ever produced, swap these imports for it - a real SVG would also
 * let the mark recolour for dark mode, which a raster cannot.
 *
 * ── Dark mode, and the glow behind it ───────────────────────────────────────
 *
 * The artwork is a raster with baked-in colour, so it cannot recolour for the
 * theme: the wordmark is near-black and on a dark surface it simply vanished.
 *
 * The fix is a soft white light behind it rather than a solid plate. A plate
 * reads as a sticker cut out of the page - a visible rectangle with edges that
 * belong to no other element on screen. A blurred radial glow has no edge, so
 * it looks like the mark is lit rather than mounted, and it gives the dark
 * letterforms something to sit against.
 *
 * Light mode gets nothing. The mark was drawn for a light ground and any glow
 * there is either invisible or a smudge.
 *
 * If a true vector is ever produced this should go: recolouring the mark itself
 * is the better answer, and this exists only because a raster cannot.
 */

interface LogoProps {
  /** Mark only, for tight spaces - a collapsed sidebar, a favicon slot. */
  markOnly?: boolean;
  className?: string;
}

export function Logo({ markOnly = false, className }: LogoProps) {
  const src = markOnly ? mark : wordmark;

  return (
    <span className="relative inline-flex shrink-0 items-center">
      {/*
        Sized off the image rather than fixed, so the mark-only lockup gets a
        proportionate glow instead of one shaped for the wordmark. Inset is
        negative to let the light spill past the artwork - contained inside it,
        the edges of the image would still read as dark.
      */}
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -inset-x-3 -inset-y-2 hidden rounded-full bg-white/75 blur-lg dark:block"
      />

      <img
        src={src}
        /*
         * The wordmark spells "NuDent", so alt text would be read out twice
         * wherever the product name already appears beside it. Empty alt marks
         * it decorative; where it IS the only identification - the login card -
         * the surrounding heading carries the name.
         */
        alt=""
        // Intrinsic size, so the layout does not jump while it loads.
        width={markOnly ? 58 : 198}
        height={64}
        className={cn('relative block w-auto shrink-0 h-7', className)}
      />
    </span>
  );
}
